import { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Persona,
  PersonaSize,
  Link,
  Spinner,
  MessageBar,
  MessageBarType,
  Separator,
  TextField,
  ActionButton,
} from '@fluentui/react';
import MetaTable from '../components/MetaTable';
import { Chip, styles } from './ProposalDashboard';

/**
 * PeopleDashboard
 * --------------------------------------
 * Props:
 *   - personId (string | number) — required
 *
 * Expected API (component is resilient; it will try these in order):
 *   1) GET /api/people/:id/dashboard => {
 *        person, projects, proposals, bios // bios come from person_bio_variations joined to people
 *      }
 *   2) Fallback endpoints if #1 doesn't exist:
 *      GET /api/people/:id
 *      GET /api/people/:id/projects
 *      GET /api/people/:id/proposals
 *      GET /api/people/:id/bios    // each with proposal_id and (optionally) proposal_title
 */

// const styles = {
//   root: { padding: 16, maxWidth: 1200, margin: '0 auto' },
//   headerRow: { alignItems: 'center' },
//   title: { fontSize: 24, fontWeight: 600 },
//   subTitle: { color: '#666' },
//   sectionCard: {
//     background: 'white',
//     border: '1px solid #e1dfdd',
//     borderRadius: 8,
//     padding: 16,
//   },
//   mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' },
//   wrapText: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.45 },
//   pillRow: { flexWrap: 'wrap' },
//   pill: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: '#f3f2f1', marginRight: 8, marginBottom: 8, fontSize: 12 },
// };

export default function PeopleDashboard(props) {
  const { personId } = props.match.params;
  const [data, setData] = useState({ person: null, projects: [], proposals: [], bios: [], skills: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true); setError(null);
      try {
        const dash = await tryGet(`/api/people/${personId}/dashboard`);
        if (dash && dash.person) {
          if (!cancelled) setData(normalizeDashboard(dash));
          return;
        }
        const person = await mustGet(`/api/people/${personId}`);
        const projects = (await tryGet(`/api/people/${personId}/projects`)) || [];
        const proposals = (await tryGet(`/api/people/${personId}/proposals`)) || [];
        const bios = (await tryGet(`/api/people/${personId}/bios`)) || [];
        const skills = (await tryGet(`/api/people/${personId}/skills`)) || [];
        if (!cancelled) setData(normalizeDashboard({ person, projects, proposals, bios, skills }));
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [personId]);

  const { person, projects, proposals, bios } = data;

  // Basic person fields with robust key picking
  const name = useMemo(() => pickFirst(person, ['name','full_name','display_name']) || `Person ${personId}`, [person, personId]);
  const title = useMemo(() => pickFirst(person, ['title','role','position']), [person]);
  const designation = useMemo(() => pickFirst(person, ['designation','designations']), [person]);
  const education = useMemo(() => pickFirst(person, ['education','education_text','degrees']), [person]);
  const affiliations = useMemo(() => pickFirst(person, ['affiliations','memberships','professional_affiliations']), [person]);
  const awards = useMemo(() => pickFirst(person, ['awards','honors']), [person]);
  const employment = useMemo(() => pickFirst(person, ['employment_history','experience','work_history']), [person]);
  const photoUrl = useMemo(() => pickFirst(person, ['photo_url','photo','image_url','avatar_url']), [person]);
  const canonicalBio = useMemo(() => pickFirst(person, ['canonical_bio', 'default_bio']), [person]);

  // Canonical bio from bios list
  // const primaryBio = useMemo(() => pickPrimary(bios, (b) => b.is_canonical || /canonical/i.test(b.description_type || '')), [bios]);

  // Columns
  const projectColumns = [
    { key: 'col1', name: 'Title', fieldName: 'title', minWidth: 220, isResizable: true, onRender: (item) => (
      item.id ? <Link href={`/projects/${item.id}`}>{item.title}</Link> : <span>{item.title}</span>
    ) },
    { key: 'col2', name: 'Client', fieldName: 'client_name', minWidth: 120, isResizable: true },
    { key: 'col3', name: 'Year', fieldName: 'year', minWidth: 60, maxWidth: 80, isResizable: true },
  ];

  const proposalColumns = [
    { key: 'p1', name: 'Title', fieldName: 'title', minWidth: 220, isResizable: true, onRender: (item) => (
      item.id ? <Link href={`/proposals/${item.id}`}>{item.title}</Link> : <span>{item.title}</span>
    ) },
    { key: 'p2', name: 'Client', fieldName: 'client_name', minWidth: 120, isResizable: true },
    { key: 'p3', name: 'Year', fieldName: 'year', minWidth: 60, maxWidth: 80, isResizable: true },
  ];

  if (loading) return <Spinner label="Loading person..." />;
  if (error) return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
  if (!person) return <MessageBar>Person not found.</MessageBar>;

  return (
    <Stack styles={{ root: styles.root }} tokens={{ childrenGap: 16 }}>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: styles.headerRow }}>
        <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
          <Persona styles={{primaryText: {fontSize: 28, fontWeight: 600}}} text={name} secondaryText={`${title} - ${designation}` || ''} imageUrl={photoUrl || undefined} size={PersonaSize.size120} />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Link href={`/people/${person.id}/edit`}>
            <ActionButton iconProps={{iconName: "Edit"}} text="Edit" />
          </Link>
        </Stack>
      </Stack>

      {/* At a glance / Background */}
      <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
        <Stack grow styles={{ root: styles.sectionCard }} tokens={{childrenGap: 8}}>
          <Stack.Item>
            <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Profile</Text>
            <Separator />
            <Text>{person.default_bio}</Text>
          </Stack.Item>
          {/* <Separator /> */}
          <Stack.Item>
            <MetaTable 
              borderless
              labelColWidth={120}
              items={[
              {label: "Title", text: title},
              {label: "Designation", text: designation},
              {label: "Skills", text: data.skills.map(s => s.name).join(', '), onRender: (item) => (
                <Stack horizontal wrap tokens={{childrenGap: 8}}>
                  {data.skills.map(s => <Chip key={s.name}>{s.name}</Chip>)}
                </Stack>
              )}
            ]} />
          </Stack.Item>
        </Stack>

        <Stack grow styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
          <Stack.Item>
            <Text variant="xLarge">Background</Text>
            <Separator />
          </Stack.Item>
          <FieldBlock label="Education" multiline value={education} />
          <FieldBlock label="Affiliations" multiline value={affiliations} />
          <FieldBlock label="Awards" multiline value={awards} />
          <FieldBlock label="Employment History" multiline value={employment} />
        </Stack>
      </Stack>

      {/* Canonical Bio */}
      <Stack styles={{ root: styles.sectionCard }}>
        <Text variant="xLarge">Canonical Bio</Text>
        <Separator />
        {canonicalBio ? <ExpandableText text={canonicalBio} /> : <Text>—</Text>}
      </Stack>

      {/* Projects */}
      <Stack styles={{ root: styles.sectionCard }}>
        <Stack.Item>
          <Text variant="xLarge">Projects</Text>
          <Separator />
        </Stack.Item>
        {projects?.length ? (
          <DetailsList
            items={projects}
            columns={projectColumns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            styles={{ root: { overflowX: 'auto' } }}
          />
        ) : (
          <Text>—</Text>
        )}
      </Stack>

      {/* Proposals */}
      <Stack styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
        <Stack.Item>
          <Text variant="xLarge">Proposals</Text>
          <Separator/>
        </Stack.Item>
        {proposals?.length ? (
          <DetailsList
            items={proposals}
            columns={proposalColumns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            styles={{ root: { overflowX: 'auto' } }}
          />
        ) : (
          <Text>—</Text>
        )}
      </Stack>

      {/* All Bio Variations (with linked proposal) */}
      <BiosSection bios={bios} />
    </Stack>
  );
}

function BiosSection({ bios }) {
  const list = Array.isArray(bios) ? bios : [];
  if (!list.length) return (
    <Stack styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
      <Text variant="xLarge">Bio Variations</Text>
      <Text>—</Text>
    </Stack>
  );

  return (
    <Stack styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
      <Stack.Item>
        <Text variant="xLarge">Bio Variations</Text>
        <Separator />
      </Stack.Item>
      <Stack tokens={{ childrenGap: 12 }}>
        {list.map((b) => (
          <Stack key={b.id}>
            <Text styles={{ root: { fontWeight: 600 } }}>
              {compactMeta({
                proposal: b.proposal_title ? b.proposal_title : (b.proposal_id ? `Proposal ${b.proposal_id}` : null),
                type: b.description_type,
                created: toDateString(b.created_at),
              })}
            </Text>
            {b.proposal_id ? (
              <Text>
                <Link href={`/proposals/${b.proposal_id}`}>View Proposal</Link>
              </Text>
            ) : null}
            <Text>{b.bio_text || b.description_text || '—'}</Text>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function FieldBlock({ label, value, multiline }) {
  if (!value) return null;
  return (
    <Stack tokens={{ childrenGap: 4 }}>
      <Text styles={{ root: { fontWeight: 600 } }}>{label}</Text>
      <TextField multiline={multiline} autoAdjustHeight borderless readOnly value={value} />
    </Stack>
  );
}

function ExpandableText({ text, maxChars = 800 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <Text>—</Text>;
  const str = String(text);
  const needsTrim = str.length > maxChars && !expanded;
  const shown = needsTrim ? str.slice(0, maxChars) + '…' : str;
  return (
    <>
      <TextField multiline autoAdjustHeight readOnly value={shown} borderless />
      {needsTrim && (
        <Link onClick={() => setExpanded(true)} styles={{ root: { marginTop: 6, display: 'inline-block' } }}>Show more</Link>
      )}
    </>
  );
}

// ---------------- Helpers ----------------
async function tryGet(url) {
  try { const r = await fetch(url); if (!r.ok) return null; return await r.json(); } catch { return null; }
}
async function mustGet(url) {
  const r = await fetch(url); if (!r.ok) throw new Error(`${r.status} ${r.statusText} — ${url}`); return r.json();
}

function normalizeDashboard(input) {
  const person = input?.person || input;
  const projects = Array.isArray(input?.projects) ? input.projects : [];
  const proposals = Array.isArray(input?.proposals) ? input.proposals : [];
  const bios = Array.isArray(input?.bios) ? input.bios : [];
  const skills = input.skills || [];

  const normProjects = projects.map((p) => ({
    id: p.id,
    title: pickFirst(p, ['title', 'name', 'project_number', 'client_friendly_name']) || `(Project ${p.id})`,
    client_name: p.client_name || p.client || null,
    year: p.year || extractYear(p.summary_end_date) || extractYear(p.summary_start_date),
  }));

  const normProposals = proposals.map((pr) => ({
    id: pr.id,
    title: pickFirst(pr, ['title','name']) || `(Proposal ${pr.id})`,
    client_name: pr.client_name || pr.client || null,
    year: pr.year || extractYear(pr.date_submitted) || pr.date_submitted_year || null,
  }));

  return { person, projects: normProjects, proposals: normProposals, bios, skills };
}

function pickFirst(obj, keys) {
  if (!obj) return undefined;
  for (const k of keys) { if (obj[k] != null && obj[k] !== '') return obj[k]; }
  return undefined;
}

function extractYear(v) {
  if (!v) return undefined; const d = new Date(v); return isNaN(d) ? undefined : d.getFullYear();
}

function toDateString(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

function compactMeta(obj) {
  return Object.entries(obj).filter(([,v]) => v).map(([k,v]) => String(v)).join(' • ');
}

function pickPrimary(list, preferFn) {
  if (!Array.isArray(list) || list.length === 0) return null;
  if (typeof preferFn === 'function') {
    const chosen = list.find(preferFn);
    if (chosen) return chosen;
  }
  const canonical = list.find(d => d?.is_canonical || /canonical|primary/i.test(d?.description_type || ''));
  if (canonical) return canonical;
  const scored = [...list].map(d => ({
    d,
    s: (new Date(d?.created_at || 0).getTime() || 0) / 1e3 + (Number(d?.description_length) || String(d?.description_text || d?.bio_text || '').length || 0),
  })).sort((a,b) => b.s - a.s);
  return scored[0].d;
}

function MetaRow({ label, value, mono }) {
  return (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="baseline">
      <Text styles={{ root: { width: 140, color: '#605e5c' } }}>{label}</Text>
      <Text styles={{ root: { ...(mono ? styles.mono : {}) } }}>{value}</Text>
    </Stack>
  );
}
